import React from 'react';
import { StyleSheet, css } from 'aphrodite';
import { useNavigate } from 'react-router-dom';
import heroImage from '../../assets/home/hero-delivery.png';
import { LINKS, openExternalLink } from '../../config/links';
import { colors, shared } from '../../styles/theme';
import ArrowIcon from '../ArrowIcon';

function HeroSection() {
  const navigate = useNavigate();

  return (
    <section className={css(styles.section)}>
      <div className={css(styles.content)}>
        <p className={css(styles.eyebrow)}>Good food. Brighter together.</p>
        <h1 className={css(styles.title)}>
          Local favourites, <span className={css(styles.accent)}>delivered with YaBatho.</span>
        </h1>
        <p className={css(styles.copy)}>
          Order from restaurants near you, support local businesses, and enjoy
          reliable delivery.
        </p>
        <div className={css(styles.actions)}>
          <button
            className={css(styles.primaryButton)}
            onClick={() => openExternalLink(LINKS.customerWebApp)}
          >
            <span>Order Now</span>
            <ArrowIcon />
          </button>
          <button
            className={css(styles.secondaryButton)}
            onClick={() => navigate('/restaurants')}
          >
            Partner With Us
          </button>
        </div>
        <button className={css(styles.opportunities)} onClick={() => navigate('/careers')}>
          <span>Rider &amp; waiter opportunities</span>
          <ArrowIcon size={17} />
        </button>
      </div>

      <div className={css(styles.visual)}>
        <div className={css(styles.imageRing)}>
          <img
            className={css(styles.image)}
            src={heroImage}
            alt="A local restaurant owner handing an order to a delivery rider"
          />
        </div>
        <div className={css(styles.note)}>
          <strong>Real food.</strong>
          <span>Real people. Stronger communities.</span>
        </div>
      </div>
    </section>
  );
}

const styles = StyleSheet.create({
  section: {
    ...shared.container,
    display: 'grid',
    gridTemplateColumns: '0.92fr 1.08fr',
    gap: '64px',
    alignItems: 'center',
    minHeight: '660px',
    paddingTop: '68px',
    paddingBottom: '76px',
    '@media (max-width: 940px)': {
      gridTemplateColumns: '1fr',
      gap: '42px',
      minHeight: 0,
      paddingTop: '52px',
    },
  },
  content: { minWidth: 0 },
  eyebrow: shared.eyebrow,
  title: {
    maxWidth: '650px',
    margin: '0 0 24px',
    color: colors.ink,
    fontSize: 'clamp(2.8rem, 5.2vw, 5.25rem)',
    fontWeight: '900',
    letterSpacing: '-0.055em',
    lineHeight: 0.98,
    '@media (max-width: 520px)': {
      fontSize: '2.15rem',
      letterSpacing: '-0.035em',
      lineHeight: 1.04,
    },
  },
  accent: { color: colors.gold },
  copy: {
    maxWidth: '590px',
    margin: '0 0 30px',
    color: colors.muted,
    fontSize: '1.14rem',
    lineHeight: 1.65,
    '@media (max-width: 520px)': {
      fontSize: '1rem',
    },
  },
  actions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '12px',
    maxWidth: '520px',
    '@media (max-width: 560px)': { gridTemplateColumns: '1fr' },
  },
  primaryButton: {
    ...shared.primaryButton,
    minHeight: '58px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    border: `1px solid ${colors.gold}`,
  },
  secondaryButton: {
    ...shared.secondaryButton,
    minHeight: '58px',
    borderColor: colors.ink,
    backgroundColor: 'transparent',
  },
  opportunities: {
    marginTop: '20px',
    padding: 0,
    border: 0,
    borderBottom: `1px solid ${colors.gold}`,
    backgroundColor: 'transparent',
    color: colors.ink,
    fontSize: '0.92rem',
    fontWeight: '900',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
  },
  visual: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    '@media (max-width: 940px)': {
      flexDirection: 'column',
      alignItems: 'center',
      flexWrap: 'nowrap',
    },
  },
  imageRing: {
    width: 'min(100%, 570px)',
    aspectRatio: '1 / 1',
    overflow: 'hidden',
    borderRadius: '50%',
    border: `1px solid ${colors.gold}`,
    padding: '10px',
    backgroundColor: colors.sand,
    boxSizing: 'border-box',
  },
  image: {
    width: '100%',
    height: '100%',
    display: 'block',
    objectFit: 'cover',
    objectPosition: 'center',
    borderRadius: '50%',
  },
  note: {
    position: 'absolute',
    left: 0,
    bottom: '9%',
    display: 'grid',
    gap: '3px',
    maxWidth: '230px',
    padding: '16px 18px',
    border: `1px solid ${colors.gold}`,
    borderRadius: '8px',
    backgroundColor: colors.ink,
    color: colors.white,
    boxShadow: '0 16px 34px rgba(15, 61, 46, 0.2)',
    '@media (max-width: 940px)': {
      position: 'relative',
      left: 'auto',
      bottom: 'auto',
      width: 'min(100% - 28px, 500px)',
      maxWidth: '500px',
      margin: '18px auto 0',
      boxSizing: 'border-box',
    },
    '@media (max-width: 520px)': {
      width: 'calc(100% - 28px)',
      maxWidth: 'none',
      margin: '18px 14px 0',
    },
  },
});

export default HeroSection;
